import React, { useState, useMemo, useCallback } from 'react';
import { Typography, Tooltip, Box, IconButton, Alert, Button } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';

import useTranslation from '../customHooks/translations';
import CreateStudentAccountModal from './CreateStudentAccountModal';

const COPY_RESET_MS = 2500;

const AddMemberPanel = ({ groupInfo, refetchMembers }) => {
  const translation = useTranslation();
  const [copySuccess, setCopySuccess] = useState({
    link: false,
    password: false,
  });
  const [openModal, setOpenModal] = useState(false);

  const handleOpen = () => setOpenModal(true);
  const handleClose = () => setOpenModal(false);

  // Stable invite token — regenerating every render would change the link on each paint
  const inviteToken = useMemo(
    () => uuidv4(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupInfo?.group_id]
  );

  const groupLink = useMemo(() => {
    if (!groupInfo) return null;
    const { origin } = window.location;
    const groupPath = `/group-profile/${encodeURIComponent(groupInfo.group_name)}`;
    return `${origin}${groupPath}/add/${inviteToken}-${groupInfo.group_id ** 2}`;
  }, [groupInfo, inviteToken]);

  const copyText = useCallback(async (text, type) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess((prev) => ({ ...prev, [type]: true }));
      setTimeout(() => {
        setCopySuccess((prev) => ({ ...prev, [type]: false }));
      }, COPY_RESET_MS);
    } catch {
      // Leave success false if clipboard write fails
    }
  }, []);

  return (
    <Box
      display="flex"
      justifyContent="center"
      flexDirection="column"
      alignItems={{ xs: 'center', lg: 'flex-start' }}
    >
      <Typography component="div" variant="h4" sx={{ m: '2em 0 1em 0', color: '#0F172A' }}>
        {translation.existingUsersTitle}
      </Typography>
      <Typography component="div" variant="h3">
        {groupInfo.is_public ? translation.addMembersDescription : translation.addMembersDescriptionPassword}
      </Typography>
      <Typography component="div" variant="subtitle2" sx={{ m: '2em 0 1em 0' }}>
        {translation.groupLinkLabel}{' '}
      </Typography>
      <Box
        display="flex"
        sx={{
          flexDirection: { xs: 'column', md: 'row' },
          wordBreak: 'break-word',
        }}
      >
        <Typography
          component="div"
          variant="subtitle2"
          sx={{
            border: '1px black solid',
            borderRadius: '2px',
            pl: '1em',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {groupLink}
          <Tooltip title="Copy Link">
            <IconButton
              aria-label="copy link"
              onClick={() => copyText(groupLink, 'link')}
            >
              <ContentCopy
                sx={{
                  alignSelf: 'center',
                  ':hover': { cursor: 'pointer', opacity: '0.5' },
                }}
              />
            </IconButton>
          </Tooltip>
        </Typography>
        {copySuccess.link && (
          <Alert
            severity="success"
            onClose={() => setCopySuccess((prev) => ({ ...prev, link: false }))}
            sx={{ ml: '1em', mt: { xs: '1em', md: '0' } }}
          >
            {translation.linkCopied}
          </Alert>
        )}
      </Box>
      {!groupInfo.is_public && (
        <>
          <Typography
            component="div"
            variant="subtitle2"
            sx={{ m: '2em 0 1em 0' }}
          >
            {translation.groupPasswordLabel}{' '}
          </Typography>
          <Box
            display="flex"
            sx={{
              flexDirection: { xs: 'column', md: 'row' },
              wordBreak: 'break-word',
            }}
          >
            <Typography
              component="div"
              variant="subtitle2"
              sx={{
                border: '1px black solid',
                borderRadius: '2px',
                pl: '1em',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {groupInfo.private_password}
              <Tooltip title="Copy Password">
                <IconButton
                  aria-label="copy password"
                  onClick={() => copyText(groupInfo.private_password, 'password')}
                >
                  <ContentCopy
                    sx={{
                      alignSelf: 'center',
                      ':hover': { cursor: 'pointer', opacity: '0.5' },
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Typography>
            {copySuccess.password && (
              <Alert
                severity="success"
                onClose={() =>
                  setCopySuccess((prev) => ({ ...prev, password: false }))
                }
                sx={{ ml: '1em', mt: { xs: '1em', md: '0' } }}
              >
                {translation.passwordCopied}
              </Alert>
            )}
          </Box>
        </>
      )}
      <Typography component="div" variant="h4" sx={{ m: '2em 0 1em 0', color: '#0F172A' }}>
        {translation.educatorsTitle}
      </Typography>
      <Button variant="contained" onClick={handleOpen} sx={{paddingInline: '2.5rem'}}>
        {translation.createStudentAccountButton}
      </Button>

      <CreateStudentAccountModal
        groupInfo={groupInfo}
        open={openModal}
        refetchMembers={refetchMembers}
        onClose={handleClose}
      />
    </Box>
  );
};

export default AddMemberPanel;
